import type { ApprovalDecisionState, ApprovalInboxItem } from '@servicetim/shared';
import { useApprovals } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  pagedTableProps,
} from '@servicetim/ui/components';
import { Link } from '@tanstack/react-router';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { SimpleSelect } from '../projects/ui/simple-select';
import { relativeTime } from '../tenant/common/format';
import { ApprovalDecisionDialog, type ApprovalDecisionTarget } from './decision-dialog';

const DECISION_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const DECISION_VARIANT: Record<ApprovalDecisionState, 'warning' | 'success' | 'destructive'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'destructive',
};

/** `GET /v1/approvals?mine=true`: the caller's own pending/decided approver rows (S-07-10). */
export function ApprovalsInbox() {
  const [decision, setDecision] = useState<ApprovalDecisionState>('pending');
  const [target, setTarget] = useState<ApprovalDecisionTarget | null>(null);
  const approvals = useApprovals({ mine: true, decision });

  const columns = useMemo<ColumnDef<ApprovalInboxItem, unknown>[]>(
    () => [
      {
        id: 'ticket',
        header: 'Ticket',
        enableSorting: false,
        cell: ({ row }) => (
          <Link
            to="/tickets/$idOrKey"
            params={{ idOrKey: row.original.ticket.key }}
            className="font-mono text-accent text-xs underline-offset-4 hover:underline"
          >
            {row.original.ticket.key}
          </Link>
        ),
      },
      {
        id: 'summary',
        header: 'Summary',
        enableSorting: false,
        cell: ({ row }) => <span className="truncate text-fg">{row.original.ticket.summary}</span>,
      },
      {
        accessorKey: 'decision',
        header: 'Decision',
        cell: ({ row }) => (
          <Badge variant={DECISION_VARIANT[row.original.decision]}>{row.original.decision}</Badge>
        ),
      },
      {
        accessorKey: 'requestedAt',
        header: 'Age',
        cell: ({ row }) => (
          <time dateTime={row.original.requestedAt} title={row.original.requestedAt}>
            {relativeTime(row.original.requestedAt)}
          </time>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.decision === 'pending' ? (
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setTarget({
                    ticketId: row.original.ticketId,
                    approvalId: row.original.id,
                    decision: 'rejected',
                    ticketKey: row.original.ticket.key,
                  })
                }
              >
                <ThumbsDown /> Reject
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  setTarget({
                    ticketId: row.original.ticketId,
                    approvalId: row.original.id,
                    decision: 'approved',
                    ticketKey: row.original.ticket.key,
                  })
                }
              >
                <ThumbsUp /> Approve
              </Button>
            </div>
          ) : null,
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="max-w-xs">
        <SimpleSelect
          aria-label="Filter by decision"
          value={decision}
          options={DECISION_OPTIONS}
          onChange={(next) => next && setDecision(next as ApprovalDecisionState)}
        />
      </div>
      <DataTable
        columns={columns}
        {...pagedTableProps(approvals)}
        getRowId={(a) => a.id}
        caption="Approvals"
        emptyTitle={decision === 'pending' ? 'Nothing to approve' : `No ${decision} approvals`}
        emptyDescription="Requests where you are an approver show up here."
      />
      <ApprovalDecisionDialog target={target} onClose={() => setTarget(null)} />
    </div>
  );
}
